import React from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { useLabels } from 'ws-scripts/modules/common';
import { useCurrentPath } from '../context/CurrentPathContext';
import { buildNavUrl, isNavItemActive } from '../utils/navItemUtils';
import NavItemLabel from './NavItemLabel';

/**
 * NavLink — renders a simple anchor link for a navigation item.
 *
 * When rendered via NavItem (top-level), isActive/isLeafActive are passed
 * from the orchestrator. When rendered directly by DrilldownLeafLink or
 * DrilldownGroup, NavLink computes them internally as a fallback.
 *
 * @param {Object} props
 * @param {NavigationItem} props.item - Navigation item data
 * @param {() => void} props.onLinkClick - Called when the link is clicked
 * @param {boolean} [props.isActive] - Whether this item or an ancestor is active (from orchestrator)
 * @param {boolean} [props.isLeafActive] - Whether this specific item is the current page (from orchestrator)
 * @param {string} [props.labelClassName] - Additional class name applied to the label span
 */
const NavLink = ({
	item,
	onLinkClick,
	isActive,
	isLeafActive,
	labelClassName = ''
}) => {
	const labels = useLabels();
	const opensInNewTabLabel =
		labels.get('OPENS_IN_NEW_TAB') || 'opens in new tab';
	const currentPath = useCurrentPath();

	// Fall back to self-computed active state when not provided by orchestrator
	const computedActive =
		isActive !== undefined ? isActive : isNavItemActive(item, currentPath);
	const computedLeafActive =
		isLeafActive !== undefined
			? isLeafActive
			: isNavItemActive(item, currentPath);

	const isExternal = item.target === '_blank';
	const activeClass = computedActive ? 'ws-nav-drawer__nav-item--active' : '';

	const handleClick = onLinkClick;

	return (
		<a
			href={buildNavUrl(item)}
			target={item.target || '_self'}
			rel={isExternal ? 'noopener noreferrer' : undefined}
			className={setClassNames([
				'ws-nav-drawer__nav-item',
				'ws-nav-drawer__nav-item--link',
				'py-4',
				'd-flex',
				'align-items-center',
				'w-100',
				'bg-transparent',
				'text-decoration-none',
				'text-left',
				activeClass
			])}
			aria-current={computedLeafActive ? 'page' : undefined}
			onClick={handleClick}
		>
			<NavItemLabel label={item.label} className={labelClassName} />
			{isExternal && (
				<>
					<i
						className={setClassNames([
							'ddc-icon',
							'ddc-icon-launch',
							'ws-nav-drawer__nav-item-icon',
							'ddc-font-size-large',
							'd-flex',
							'flex-shrink-0',
							'align-items-center',
							'justify-content-center'
						])}
						aria-hidden="true"
					/>
					<span className="sr-only">{opensInNewTabLabel}</span>
				</>
			)}
		</a>
	);
};

NavLink.propTypes = {
	item: PropTypes.shape({
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		target: PropTypes.string,
		selected: PropTypes.bool,
		matched: PropTypes.bool
	}).isRequired,
	onLinkClick: PropTypes.func.isRequired,
	isActive: PropTypes.bool,
	isLeafActive: PropTypes.bool,
	labelClassName: PropTypes.string
};

export default NavLink;
